import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { contractsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Edit, Ban, CheckCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadCustomer(); }, [id]);

  const loadCustomer = async () => {
    try {
      const res = await contractsAPI.getCustomer(id);
      setCustomer(res.data);
    } catch { toast.error('Customer not found'); navigate('/contracts/customers'); }
    finally { setLoading(false); }
  };

  const handleSuspend = async () => {
    const action = customer.status === 'active' ? 'Suspend' : 'Activate';
    if (!window.confirm(`${action} ${customer.customer_name}?`)) return;
    try {
      const res = await contractsAPI.suspendCustomer(id);
      setCustomer(prev => ({ ...prev, status: res.data.status }));
      toast.success(`Customer ${action.toLowerCase()}d`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;
  if (!customer) return null;

  return (
    <div className="space-y-4" data-testid="customer-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts/customers')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-xl font-bold tracking-tight">{customer.customer_name}</h1>
              <Badge variant="outline" className={`text-[10px] ${customer.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                {customer.status}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground font-mono">{customer.customer_code}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/customers/${id}/edit`)} data-testid="edit-customer-btn">
            <Edit className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          {isAdmin && (
            <Button size="sm" variant={customer.status === 'active' ? 'destructive' : 'default'} onClick={handleSuspend} data-testid="suspend-btn">
              {customer.status === 'active' ? <><Ban className="w-3.5 h-3.5 mr-1" /> Suspend</> : <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Activate</>}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Contact Info</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <InfoRow label="Contact Person" value={customer.contact_person} />
            <InfoRow label="Phone" value={customer.phone} />
            <InfoRow label="Email" value={customer.email} />
            <InfoRow label="Website" value={customer.website} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Tax & Compliance</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <InfoRow label="GST" value={customer.gst_number} mono />
            <InfoRow label="PAN" value={customer.pan_number} mono />
          </CardContent>
        </Card>
        <Card className="md:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Address</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <InfoRow label="Address" value={[customer.address_1, customer.address_2].filter(Boolean).join(', ') || '-'} />
            <InfoRow label="City" value={customer.city} />
            <InfoRow label="State" value={customer.state_province} />
            <InfoRow label="Country" value={customer.country} />
            <InfoRow label="PIN" value={customer.pin_code} />
          </CardContent>
        </Card>
      </div>

      {customer.purchase_orders?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Purchase Orders ({customer.purchase_orders.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-2.5 font-medium text-muted-foreground">PO#</th>
                  <th className="text-left p-2.5 font-medium text-muted-foreground">Date</th>
                  <th className="text-left p-2.5 font-medium text-muted-foreground">Project</th>
                  <th className="text-left p-2.5 font-medium text-muted-foreground">Value</th>
                  <th className="text-left p-2.5 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {customer.purchase_orders.map(po => (
                  <tr key={po.id} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/contracts/purchase-orders/${po.id}`)}>
                    <td className="p-2.5 font-mono font-medium">{po.po_number}</td>
                    <td className="p-2.5">{po.po_date}</td>
                    <td className="p-2.5">{po.project_number || '-'}</td>
                    <td className="p-2.5 font-medium">{po.value ? `Rs ${po.value.toLocaleString()}` : '-'}</td>
                    <td className="p-2.5"><Badge variant="outline" className="text-[10px]">{po.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {customer.remarks && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">{customer.remarks}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className={`text-xs font-medium text-right ${mono ? 'font-mono' : ''}`}>{value || '-'}</span>
    </div>
  );
}
